/* ==========================================================================
   GB PRE-PURCHASE UPSELL — controller (metafield-driven + multi-product)
   Custom replacement for Zipify OCU "pre-purchase" offer.

   Data: window.GBPPU_CONFIG (assets/gb-ppu-config.js), generated from each
   product's custom.collection_upsells_3 (same source as the PDP "Gear It Up"
   chips) + the hidden discount twins.

   Behaviour:
     • Fires on the real Checkout click (cart drawer OR cart page) whenever the
       cart holds >=1 TRIGGER blaster that still has >=1 add-on not in the cart.
     • Multi-product: shows the UNION of every in-cart blaster's add-on
       collection, deduped, hide-in-cart, ordered by priority, capped.
     • Each "Add" adds the DISCOUNTED twin variant (real saving at checkout).
     • Instant optimistic add. X / tap-outside -> back to cart.
       "Decline this offer" / "Checkout" -> straight to checkout.

   HARD SAFETY: a script error can NEVER block native checkout. We only
   preventDefault when sure we're opening (sync decision from a cached cart);
   cold cache blocks only behind a strict timeout that ALWAYS proceeds. We never
   tear down <cart-drawer>; cart refresh swaps only the single items node.
   ========================================================================== */
(function () {
  'use strict';
  if (window.GBPPU) return;

  var CHECKOUT_SELECTOR =
    '#CartDrawer-Checkout, #checkout, button[name="checkout"], input[name="checkout"]';
  var CART_EVENTS = ['cart:refresh', 'cart:updated', 'cart:change', 'cart:added', 'cart:build'];

  // Multi-currency / Shopify Markets: config prices are in shop-base cents (USD).
  // Convert to the shopper's PRESENTMENT currency via Shopify.currency.rate and format
  // in their currency — same house pattern as the custom free-ship bar (gbfs).
  function ppuRate() {
    try { var r = parseFloat(window.Shopify && Shopify.currency && Shopify.currency.rate); return r > 0 ? r : 1; }
    catch (e) { return 1; }
  }
  function ppuCurrency() {
    try { return (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD'; } catch (e) { return 'USD'; }
  }
  function money(baseCents) {
    // Congruence: one shared price formatter across all builds (no PPU-vs-bar drift); fallback to own.
    if (window.GB && window.GB.PriceFormatter) return window.GB.PriceFormatter.format(baseCents);
    var amount = (Math.round(baseCents || 0) * ppuRate()) / 100;
    var cur = ppuCurrency();
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(amount); }
    catch (e) { return '$' + amount.toFixed(2); }
  }
  // Free-shipping bar: work in BASE USD cents end-to-end (cart subtotal is presentment -> divide by rate ONCE),
  // then format the gap with money() (which re-applies the rate). Never pass presentment cents to money().
  var FREESHIP_BASE_CENTS = 7000;       // $70 threshold (matches the cart's gbfs bar), BASE USD cents
  var FREESHIP_MARKER_WINDOW = 2000;    // show the per-card 'unlocks' marker only within $20 (base) of the goal
  function imgUrl(u, w) {
    if (!u) return '';
    if (u.indexOf('//') === 0) u = 'https:' + u;
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + (w || 220);
  }

  var GBPPU = {
    cfg: null, root: null, panel: null, list: null,
    triggers: null, offers: null, maxVisible: 8,
    cart: null, open: false, deciding: false, cartDirty: false, cartPending: false, bypass: false,
    _fsLockOnOpen: false, _fsPendingBaseCents: 0, _fsExpectedItems: 0,   // free-ship: locked-on-open flag + optimistic add accumulator + expected cart item_count (reconciliation gate)
    pending: [], timerInt: null, sourceBtn: null, lastFocus: null,
    SS_TIMER: 'gbppu_timer_start',

    init: function () {
      try {
        this.cfg = window.GBPPU_CONFIG || null;
        this.root = document.getElementById('GBPPU-Root');
        if (!this.cfg || !this.root) return;
        if (this._gbInited) return;   // guard against duplicate listener registration on any re-init
        this._gbInited = true;
        this.panel = this.root.querySelector('.gbppu-panel');
        this.list = this.root.querySelector('[data-gbppu-list]');
        this.triggers = this.cfg.triggers || {};
        this.offers = (this.cfg.offers || []).slice();
        if (typeof this.cfg.maxVisible === 'number') this.maxVisible = this.cfg.maxVisible;

        document.addEventListener('click', this.onCheckoutClick.bind(this), true);
        // SUBMIT-GUARD: the theme navigates by SUBMITTING the cart form (#CartDrawer-Checkout is
        // type=submit; the cart page does #checkout.click -> #cart.submit()). preventDefault on the
        // click can't stop a programmatic form.submit(), so also cancel the submit EVENT (capture)
        // and no-op the form's .submit() method while the drawer is open / mid-decision.
        document.addEventListener('submit', this.onCartSubmit.bind(this), true);
        this.guardFormSubmit();
        this.root.addEventListener('click', this.onRootClick.bind(this));
        this.root.addEventListener('keydown', this.onKeydown.bind(this));
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && GBPPU.open) GBPPU.dismiss();
        });

        var self = this;
        // PERF r10 2026-09-06: boot from the SHARED snapshot, never a hard /cart.js. gb-cart-state.js
        // seeds GBCart from the server-rendered [data-gb-addon-state] JSON the drawer already ships in
        // the HTML, so the first paint costs ZERO network. refreshCart(false) takes that lane and only
        // falls back to a real fetch when the snapshot node is missing. Was: this.refreshCart() -> one
        // unconditional /cart.js at every page load.
        this.refreshCart(false);
        // PERF: the theme fires a BURST of these 5 near-synonym cart events per single add/change.
        // Debounce so one burst = ONE /cart.js refetch (was 5 round-trips per add).
        var refreshDebounced = (function () { var t; return function () { self.cartPending = true; clearTimeout(t); t = window.setTimeout(function () { self.refreshCart(false); }, 60); }; })();
        CART_EVENTS.forEach(function (ev) { document.addEventListener(ev, refreshDebounced); });
        var cd = document.querySelector('cart-drawer');
        if (cd && window.MutationObserver) {
          new MutationObserver(function () { if (cd.classList.contains('active')) self.refreshCart(false); })
            .observe(cd, { attributes: true, attributeFilter: ['class'] });
        }

        // Neutralize the native checkout button(s) so they only OPEN the drawer. Run AFTER the theme's
        // DOMContentLoaded handlers attach (setTimeout 0 + load), then re-run on every cart re-render.
        window.setTimeout(function () { self.neutralize(); }, 0);
        window.addEventListener('load', function () { self.neutralize(); });
        if (window.MutationObserver) {
          var nzPending = false;
          new MutationObserver(function () {
            if (nzPending) return; nzPending = true;
            window.setTimeout(function () { nzPending = false; self.neutralize(); }, 30);
          }).observe(document.body, { childList: true, subtree: true });
        }

        // honor a checkout tap that the snippet's parse-time early gate caught before we finished loading
        if (window.__GBPPU_PENDING) {
          var pend = window.__GBPPU_PENDING; window.__GBPPU_PENDING = null;
          if (window.__GBPPU_FALLBACK) { clearTimeout(window.__GBPPU_FALLBACK); window.__GBPPU_FALLBACK = null; }
          window.setTimeout(function () { self.onOpen(pend); }, 0);
        }
        window.addEventListener('focus', function () { self.refreshCart(); });
        // bfcache restore (Back from checkout) preserves the exact pre-navigation JS + DOM state.
        // Reset the drawer first so its PROCESSING state cannot stay armed and block the next checkout.
        // Then re-render the cart from the server so just-added items show without a manual reload.
        window.addEventListener('pageshow', function (e) {
          var historyRestore = !!(e && e.persisted);
          try {
            var nav = window.performance && window.performance.getEntriesByType && window.performance.getEntriesByType('navigation')[0];
            if (nav && nav.type === 'back_forward') historyRestore = true;
          } catch (x) {}
          if (historyRestore) self.resetAfterNavigation();
          // PERF r10 2026-09-06: pageshow fires on EVERY first load too, not only on a bfcache restore,
          // so an unconditional refreshCart() bought a SECOND /cart.js on every cold page load. Only a
          // real history restore can hold a stale in-memory cart, so only that case forces the network
          // read; a cold load reuses the same server-rendered snapshot the init above already consumed.
          self.refreshCart(historyRestore ? undefined : false);
          if (historyRestore) {
            if (document.querySelector('cart-drawer-items') || document.querySelector('#CartDrawer-CartItems')) {
              self.refreshNativeCart();
            } else if (/(^|\/)cart(\/|$|\?)/.test(window.location.pathname)) {
              try { window.location.reload(); } catch (x) {}
            }
          }
        });
      } catch (e) { if (window.console) console.warn('[GBPPU] init', e); }
    },

    /* ---------------- cart helpers ------------------------------------- */
    refreshCart: function (force) {
      var self = this;
      if (force === false && window.GBCart && typeof window.GBCart.readCart === 'function') {
        return window.GBCart.readCart().then(function (c) { return self.applyFreshCart(c); }).catch(function () { return self.refreshCart(true); });
      }
      // Congruence: share the ONE debounced cart read with all builds via the cart bus (fallback to own fetch).
      // force:true — the bus caches /cart.js, and a THEME-side edit (remove gift-wrap, change qty in the cart
      // drawer) does NOT invalidate that cache, so a plain getCart() would hand us a STALE cart and the
      // free-ship bar would freeze on the pre-edit number. Always re-read the REAL cart; the bus still dedupes
      // concurrent reads and repopulates its cache fresh for every other build.
      if (window.GB && window.GB.CartBus) {
        return window.GB.CartBus.getCart({ source: 'ppu', force: true }).then(function (c) { return self.applyFreshCart(c); });
      }
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) { return self.applyFreshCart(c); })
        .catch(function () { self.cartPending = false; return null; });
    },
    // Apply a freshly-fetched REAL cart: the optimistic add accumulator is now folded into the server cart, so
    // zero it; and while the drawer is open but the shopper hasn't added anything yet, RE-EVALUATE the
    // already-unlocked-on-open lock from the real cart (a warm open may have set it from a stale cached cart
    // that has since dropped below the threshold — never let a stale lock permanently suppress the bar).
    applyFreshCart: function (c) {
      this.cartPending = false;
      // Only fold the server cart in + clear the optimistic accumulator once it actually REFLECTS the in-flight
      // adds (item_count has caught up to what we optimistically added). A stale/partial cart racing an in-flight
      // add (e.g. the warm-open /cart.js that started BEFORE the add) is ignored for the bar, so it can never
      // paint a backward gap; we keep showing (pre-add cart + accumulator) until the real cart catches up.
      var reflects = !this.cartDirty || !c || typeof c.item_count !== 'number' || c.item_count >= (this._fsExpectedItems || 0);
      if (c && reflects) { this.cart = c; this._fsPendingBaseCents = 0; }
      if (this.open) {
        if (!this.cartDirty) { var st = this.freeShipState(); this._fsLockOnOpen = !!(st && st.done); }
        this.paintFreeShip(this._fsPendingBaseCents || null);
      }
      return c;
    },

    cartIds: function (cart) {
      var ids = new Set();
      if (cart && cart.items) {
        cart.items.forEach(function (it) { ids.add(String(it.product_id)); ids.add(String(it.variant_id)); });
      }
      return ids;
    },

    // collection handles contributed by the trigger blasters currently in the cart
    // triggers[pid] = { coll, type }  (v2 — was a bare collection-handle string)
    cartTriggerColls: function (cart) {
      var set = new Set(), trig = this.triggers;
      if (cart && cart.items) {
        cart.items.forEach(function (it) {
          var t = trig[String(it.product_id)];
          if (t && t.coll) set.add(t.coll);
        });
      }
      return set;
    },

    // unique blaster TYPES in the cart that THIS add-on actually fits (its colls ∩ in-cart blaster's coll)
    offerTypes: function (o, cart) {
      var out = [], seen = {}, trig = this.triggers, colls = o.colls || [], fit = o.fitTypes || null;
      if (cart && cart.items) {
        cart.items.forEach(function (it) {
          var t = trig[String(it.product_id)];
          // type-restricted add-on (e.g. holster=handguns only) only lists/fits its allowed types
          if (t && t.coll && colls.indexOf(t.coll) > -1 && (!fit || fit.indexOf(t.type) > -1) && !seen[t.type]) { seen[t.type] = 1; out.push(t.type); }
        });
      }
      return out;
    },
    joinTypes: function (t) {
      var LAB = { 'water balloons': 'water blaster' };   // tidy awkward raw type tokens
      var seen = {}, u = [];
      t.forEach(function (x) { var l = LAB[x] || x; if (!seen[l]) { seen[l] = 1; u.push(l); } });
      t = u;
      if (!t.length) return '';
      if (t.length === 1) return t[0];
      if (t.length <= 4) return t.slice(0, -1).join(', ') + ' & ' + t[t.length - 1];
      return t.slice(0, 3).join(', ') + ' & ' + (t.length - 3) + ' more';
    },

    // coveredBy (optional config field): product IDs of bundle products that already contain this add-on,
    // e.g. the Raven Full Bundle Gear pack. While one of them is in the cart the offer counts as owned.
    offerInCart: function (o, ids) {
      return ids.has(String(o.sourceProductId)) || ids.has(String(o.sourceVariantId)) ||
             ids.has(String(o.twinProductId)) || ids.has(String(o.variantId)) ||
             !!(o.coveredBy && o.coveredBy.some(function (id) { return ids.has(String(id)); }));
    },

    // variant-gated add-on (config field requireVariantIds, e.g. the Raven Brick metal-finish upgrade):
    // true when the cart holds at least one of those exact variants. Offers without the field are unaffected.
    cartHasVariant: function (cart, vids) {
      if (!cart || !cart.items || !vids || !vids.length) return false;
      var want = {};
      vids.forEach(function (v) { want[String(v)] = 1; });
      return cart.items.some(function (it) { return !!want[String(it.variant_id)] && it.quantity !== 0; });
    },

    // the add-ons to show for THIS cart (union of in-cart blasters' collections)
    resolveOffers: function (cart) {
      var colls = this.cartTriggerColls(cart);
      if (!colls.size) return [];
      // per-trigger offer allow-list (config-driven): if any in-cart blaster pins offerKeys,
      // only those offers may render — used to mirror the PDP "Gear Up" carousel exactly.
      var keys = null, trig = this.triggers;
      if (cart && cart.items) {
        cart.items.forEach(function (it) {
          var t = trig[String(it.product_id)];
          if (t && t.offerKeys) {
            if (!keys) keys = {};
            t.offerKeys.forEach(function (k) { keys[k] = 1; });
          }
        });
      }
      var ids = this.cartIds(cart), self = this;
      var list = this.offers.filter(function (o) {
        if (self.offerInCart(o, ids)) return false;
        if (keys && !keys[o.key]) return false;
        if (o.requireVariantIds && o.requireVariantIds.length && !self.cartHasVariant(cart, o.requireVariantIds)) return false;
        var match = false;
        for (var i = 0; i < o.colls.length; i++) { if (colls.has(o.colls[i])) { match = true; break; } }
        if (!match) return false;
        // type-restricted add-on (e.g. holster) -> hide unless a compatible blaster type is in the cart
        if (o.fitTypes && self.offerTypes(o, cart).length === 0) return false;
        return true;
      });
      list.sort(function (a, b) { return (a.priority || 50) - (b.priority || 50); });  // ammo (low prio) first, never cut
      return list.slice(0, this.maxVisible);
    },

    tncBlocking: function () {
      // 'tnc-chekcbox' is the theme's real (typo'd) element; also match a future-corrected 'tnc-checkbox'.
      var tnc = document.querySelector('tnc-chekcbox[data-checked], tnc-checkbox[data-checked]');
      return !!(tnc && tnc.getAttribute('data-checked') !== 'true');
    },

    /* ---------------- checkout interception (fail-safe) ---------------- */
    onCheckoutClick: function (e) {
      try {
        var btn = e.target.closest ? e.target.closest(CHECKOUT_SELECTOR) : null;
        if (!btn) return;
        if (btn.closest('.cart__dynamic-checkout-buttons, .additional-checkout-buttons, .shopify-payment-button')) return;
        this.guardFormSubmit();   // (re)arm the form.submit() no-op in case the cart form was re-rendered
        if (this.bypass) { this.bypass = false; return; }
        // drawer already open / mid-decision -> HARD-block the native checkout. A 2nd ghost/double-tap
        // must NOT slip through to the theme's inline onclick=checkoutLoad and navigate away.
        if (this.open || this.deciding) { e.preventDefault(); e.stopImmediatePropagation(); return; }
        if (this.tncBlocking()) return;

        var self = this, cart = this.cart;

        // warm cache -> decide synchronously (but NOT if a refresh is pending = cart may be stale -> fall to cold refetch)
        if (cart && cart.items && !this.cartPending) {
          var offers = this.resolveOffers(cart);
          if (offers.length < 1) { this.refreshCart(); return; }
          e.preventDefault(); e.stopImmediatePropagation();
          try { this.openDrawer(btn, offers); } catch (err) { this.proceed(btn); }
          this.refreshCart();
          return;
        }

        // cold cache -> block behind a strict timeout that ALWAYS proceeds
        e.preventDefault(); e.stopImmediatePropagation();
        this.deciding = true; this.sourceBtn = btn;
        var done = false;
        var finish = function (offers) {
          if (done) return; done = true; self.deciding = false;
          if (offers && offers.length) { try { self.openDrawer(btn, offers); } catch (err) { self.proceed(btn); } }
          else { self.proceed(btn); }
        };
        var to = setTimeout(function () { finish(null); }, 1800);
        this.refreshCart().then(function (c) {
          clearTimeout(to);
          var offers = null;
          try { offers = c ? self.resolveOffers(c) : null; } catch (e) { offers = null; }
          finish(offers);   // any error -> proceed to native checkout, never strand
        }).catch(function () { clearTimeout(to); finish(null); });
      } catch (err) {
        this.deciding = false; this.bypass = false;   // never strand the guard/flags on an unexpected throw
        if (window.console) console.warn('[GBPPU] intercept passthrough', err);
      }
    },

    // SUBMIT-GUARD helpers — block native cart-form submission while we're showing/deciding the drawer.
    // bypass=true is set by proceed()/continueCheckout() for the legit re-trigger, which must pass through.
    isBlockingSubmit: function () { return (this.open || this.deciding) && !this.bypass; },
    onCartSubmit: function (e) {
      try {
        var f = e.target;
        var isCart = f && (f.id === 'cart' || f.id === 'CartDrawer-Form' || /^\/cart(\?|$|#)/.test(f.getAttribute('action') || ''));
        if (!isCart) return;
        if (this.isBlockingSubmit()) { e.preventDefault(); e.stopImmediatePropagation(); }
      } catch (err) {}
    },
    guardFormSubmit: function () {
      var self = this;
      ['cart', 'CartDrawer-Form'].forEach(function (id) {
        var f = document.getElementById(id);
        if (f && !f._gbSubmitGuarded) {
          var native = f.submit;   // catches the programmatic #cart.submit() that preventDefault can't stop
          f.submit = function () { if (self.isBlockingSubmit()) return; return native.apply(this, arguments); };
          f._gbSubmitGuarded = true;
        }
      });
    },

    // PRIMARY mechanism (Emi's model): the "Proceed to Checkout" button must NOT be the real checkout.
    // We REPLACE it with a clone that has NONE of the theme's listeners (no onclick=checkoutLoad, no
    // `#checkout.click -> #cart.submit()` handler) and is type=button (it can't submit the cart form).
    // Its ONLY job is to open our drawer; the drawer's own CHECKOUT button then navigates to /checkout.
    // Re-applied on every cart re-render via the body MutationObserver.
    neutralize: function () {
      var self = this;
      var nodes = document.querySelectorAll(CHECKOUT_SELECTOR);
      for (var i = 0; i < nodes.length; i++) {
        var btn = nodes[i];
        if (btn._gbN) continue;
        if (btn.closest('.cart__dynamic-checkout-buttons, .additional-checkout-buttons, .shopify-payment-button')) continue;
        var clone = btn.cloneNode(true);          // cloneNode drops every attached listener
        try { clone.type = 'button'; } catch (e) {}   // no longer a form-submit trigger
        clone.removeAttribute('onclick');             // drop the theme's inline checkoutLoad
        clone._gbN = true;
        clone.addEventListener('click', function (e) { e.preventDefault(); e.stopImmediatePropagation(); self.onOpen(this); }, true);
        if (btn.parentNode) btn.parentNode.replaceChild(clone, btn);
      }
    },
    onOpen: function (btn) {
      var self = this;
      if (this.open) return;
      var cart = this.cart;
      if (cart && cart.items && !this.cartPending) {       // skip warm fast-path if a refresh is pending (cart may be stale)
        var offers = this.resolveOffers(cart);
        if (!offers.length) { this.go(); return; }       // nothing to upsell -> straight to checkout
        try { this.openDrawer(btn, offers); } catch (e) { this.go(); }
        this.refreshCart();
        return;
      }
      // cold cart: fetch, then open if we have offers, else go to checkout. A hard timeout guarantees
      // we NEVER strand the user if /cart.js hangs (neither .then nor .catch would ever fire).
      var done = false;
      var finish = function (offers) {
        if (done) return; done = true;
        if (offers && offers.length) { try { self.openDrawer(btn, offers); } catch (e) { self.go(); } }
        else self.go();
      };
      var to = setTimeout(function () { finish(null); }, 1800);
      this.refreshCart().then(function (c) {
        clearTimeout(to);
        var offers = null; try { offers = c ? self.resolveOffers(c) : null; } catch (e) {}
        finish(offers);
      }).catch(function () { clearTimeout(to); finish(null); });
    },
    go: function () {
      var self = this;
      var resetLoading = function (showError) {
        var cont = self.root && self.root.querySelector('[data-gbppu-continue]');
        var lbl = cont && cont.querySelector('.gbppu-continue__label');
        if (cont) cont.classList.remove('is-loading');
        if (lbl && cont && cont._gbWas != null) { lbl.textContent = cont._gbWas; cont._gbWas = null; }
        var host = self.root || document.querySelector('cart-drawer');
        var foot = host && host.querySelector('.gbppu-foot');
        var anchor = (host && host.querySelector('[data-gbppu-continue]')) || document.querySelector('#CartDrawer-Checkout, button[name="checkout"]');
        var error = (host && host.querySelector('[data-gbppu-addon-error]')) || document.querySelector('[data-gbppu-addon-error-global]');
        if (showError) {
          if (!error) {
            error = document.createElement('div');
            error.setAttribute('data-gbppu-addon-error-global', '');
            error.style.cssText = 'margin:8px 0;color:#b42318;font-size:12px;text-align:center;';
            var retry = document.createElement('button');
            retry.type = 'button'; retry.textContent = 'Review checkout';
            retry.style.cssText = 'margin-left:6px;text-decoration:underline;font-weight:700;';
            retry.addEventListener('click', function () { window.location.href = '/checkout'; });
            error.appendChild(document.createTextNode('Your options could not update. Retry or review checkout.'));
            error.appendChild(retry);
            if (foot) foot.appendChild(error); else if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(error, anchor.nextSibling); else document.body.appendChild(error);
          }
          return;
        }
        if (error) error.remove();
      };
      var navigate = function (ok) {
        if (ok === false) { resetLoading(true); return; }
        try { window.location.href = '/checkout'; } catch (e) { resetLoading(); }
      };
      try {
        if (window.GBDynamicAddons && typeof window.GBDynamicAddons.beforeCheckout === 'function') {
          var pending = window.GBDynamicAddons.beforeCheckout();
          if (pending && typeof pending.then === 'function') {
            pending.then(navigate).catch(function () { resetLoading(true); });
            return;
          }
        }
      } catch (e) { resetLoading(true); return; }
      navigate(true);
    },

    proceed: function (btn) {
      this.bypass = true;
      var live = (btn && document.contains(btn)) ? btn : document.querySelector(CHECKOUT_SELECTOR);
      if (live) { try { live.click(); return; } catch (e) {} }   // if the click throws, fall through to the URL fallback
      this.bypass = false;
      this.go();   // last-resort still runs the add-on gate; never bypass it with a raw URL
    },

    /* ---------------- render ------------------------------------------- */
    renderCard: function (o) {
      var card = document.createElement('div');
      card.className = 'gbppu-card';
      card.setAttribute('data-gbppu-card', '');
      card.setAttribute('data-key', o.key);
      card.setAttribute('data-variant-id', o.variantId);
      card.setAttribute('data-source-product-id', o.sourceProductId);
      card.setAttribute('data-save-cents', o.saveCents);
      card.setAttribute('data-now-cents', o.nowCents);

      var media = document.createElement('div');
      media.className = 'gbppu-card__media';
      if (o.image) {
        var img = document.createElement('img');
        img.src = imgUrl(o.image, 280); img.alt = o.title;
        img.width = 120; img.height = 120; img.loading = 'lazy'; img.decoding = 'async';
        media.appendChild(img);
      }

      var body = document.createElement('div');
      body.className = 'gbppu-card__body';
      var title = document.createElement('p');
      title.className = 'gbppu-card__title'; title.textContent = o.title;
      var price = document.createElement('p');
      price.className = 'gbppu-card__price';
      var was = document.createElement('span'); was.className = 'gbppu-was'; was.textContent = money(o.wasCents);
      var now = document.createElement('span'); now.className = 'gbppu-now'; now.textContent = money(o.nowCents);
      price.appendChild(was); price.appendChild(now);
      if (o.saveCents > 0) {
        var save = document.createElement('span'); save.className = 'gbppu-save';
        save.textContent = o.pct + '% OFF';
        price.appendChild(save);
      }
      body.appendChild(title);
      var ftypes = this.offerTypes(o, this.cart);
      if (ftypes.length && !o.noFit) { /* GB_RAVEN_EDU_NOFIT_20260904: config offers may hide the fit chip */
        var fit = document.createElement('span');
        fit.className = 'gbppu-fit';
        fit.textContent = '✓ ' + (o.attach ? 'Fits' : 'For') + ' your ' + this.joinTypes(ftypes);
        body.appendChild(fit);
      }
      if (o.badge2) {
        var badge2 = document.createElement('span');
        badge2.className = 'gbppu-badge2'; badge2.textContent = o.badge2;
        body.appendChild(badge2);
      }
      body.appendChild(price);
      if (o.blurb) {
        var blurb = document.createElement('p');
        blurb.className = 'gbppu-card__blurb'; blurb.textContent = o.blurb;
        if (o.noFit) { blurb.style.cssText += ';white-space:normal;overflow:visible;text-overflow:clip;display:block;-webkit-line-clamp:unset;max-height:none;font-size:13px;line-height:1.35'; } /* GB_RAVEN_EDU_NOFIT_20260904: full bullets */
        body.appendChild(blurb);
      }
      if (this._fsMarker && o.key === this._fsMarker) {     // quiet 'unlocks free shipping' marker on the one crossing card
        var fsm = document.createElement('span');
        fsm.className = 'gbppu-card__fsmark';
        fsm.innerHTML = '🚚 Add this → unlock FREE shipping';
        body.appendChild(fsm);
      }

      var actions = document.createElement('div');
      actions.className = 'gbppu-card__actions';
      var qty = document.createElement('div');
      qty.className = 'gbppu-qty'; qty.setAttribute('data-gbppu-qty', '');
      qty.innerHTML =
        '<button type="button" class="gbppu-qty__btn" data-gbppu-dec aria-label="Decrease quantity">−</button>' +
        '<input class="gbppu-qty__input" data-gbppu-qty-input type="text" value="1" aria-label="Quantity 1" tabindex="-1" readonly>' +
        '<button type="button" class="gbppu-qty__btn" data-gbppu-inc aria-label="Increase quantity">+</button>';
      var add = document.createElement('button');
      add.type = 'button'; add.className = 'gbppu-add'; add.setAttribute('data-gbppu-add', '');
      add.setAttribute('data-add-label', 'ADD TO ORDER'); add.setAttribute('data-added-label', 'ADDED');
      add.innerHTML = '<span class="gbppu-add__label">ADD TO ORDER</span><span class="gbppu-add__spinner" aria-hidden="true"></span>';
      actions.appendChild(qty); actions.appendChild(add);

      card.appendChild(media); card.appendChild(body); card.appendChild(actions);
      return card;
    },

    renderList: function (offers) {
      if (!this.list) return;
      this.list.innerHTML = '';
      var self = this;
      this._fsMarker = this.fsMarkerKey(offers);          // which single card gets the 'unlocks' marker
      // If the shopper ALREADY had free shipping when the drawer opened (cart ≥ threshold), never show the bar
      // — they don't need to be told they got it. We still celebrate 🎉 for anyone who CROSSES the line via an
      // in-drawer add (that's the reward that earns the add), so only suppress the already-unlocked-on-open case.
      var st0 = this.freeShipState();
      this._fsLockOnOpen = !!(st0 && st0.done);
      this._fsPendingBaseCents = 0;                       // fresh drawer session -> no pending optimistic adds yet
      this._fsExpectedItems = (this.cart && typeof this.cart.item_count === 'number') ? this.cart.item_count : 0;
      offers.forEach(function (o) { self.list.appendChild(self.renderCard(o)); });
      this.paintFreeShip(null, true);                     // initial paint, no slide-in
    },

    /* ---------------- free-shipping progress bar ----------------------- */
    freeShipState: function (extraBaseCents) {
      var rate = ppuRate() || 1;
      if (Math.abs(rate - 1) < 0.001) rate = 1;                             // snap near-1 rate -> never unlock a cent early
      var c = this.cart;
      if (!c) return null;
      // Use total_price (the DISCOUNTED cart total — exactly what the theme's own free-ship bar uses) so the
      // two bars MATCH. items_subtotal_price is BEFORE cart-level discounts and over-counts toward the goal.
      var subPres = (c.total_price != null ? c.total_price : (c.items_subtotal_price != null ? c.items_subtotal_price : 0)) || 0;
      var curBase = Math.floor(subPres / rate) + (extraBaseCents || 0);     // floor: never over-credit toward the goal
      var gapBase = FREESHIP_BASE_CENTS - curBase;
      return { curBase: curBase, gapBase: gapBase, fill: Math.min(100, Math.max(0, Math.round(curBase / FREESHIP_BASE_CENTS * 100))), done: gapBase <= 0 };
    },
    fsMarkerKey: function (offers) {
      var st = this.freeShipState();
      if (!st || st.done || st.gapBase <= 0 || st.gapBase > FREESHIP_MARKER_WINDOW) return null;   // only within $20
      var best = null;
      (offers || []).forEach(function (o) {
        if (st.curBase + (o.nowCents || 0) >= FREESHIP_BASE_CENTS && (!best || o.nowCents < best.nowCents)) best = o;
      });
      return best ? best.key : null;                       // the cheapest add-on that crosses $70
    },
    paintFreeShip: function (extraBaseCents, instant) {
      var bar = this.root && this.root.querySelector('[data-gbppu-fsbar]');
      if (!bar) return;
      if (this._fsLockOnOpen) { bar.hidden = true; return; }   // shopper already had free shipping on open -> never show
      var st = this.freeShipState(extraBaseCents);
      if (!st) { bar.hidden = true; return; }
      bar.hidden = false;
      if (instant) bar.classList.add('gbppu-fsbar--instant');
      var fill = bar.querySelector('[data-gbppu-fsfill]');
      var msg = bar.querySelector('[data-gbppu-fsmsg]');
      if (fill) fill.style.width = st.fill + '%';
      var track = bar.querySelector('.gbppu-fsbar__track'); if (track) track.setAttribute('aria-valuenow', st.fill);
      if (st.done) {
        bar.classList.add('is-done');
        if (msg) msg.innerHTML = '🎉 <b>FREE shipping unlocked!</b>';
      } else {
        bar.classList.remove('is-done');
        var gap = money(Math.max(0, st.gapBase));
        if (msg) msg.innerHTML = (st.gapBase <= 1000)
          ? 'Almost there — just <b>' + gap + '</b> from <b>FREE shipping!</b>'
          : 'Add <b>' + gap + '</b> more for <b>FREE shipping</b>';
      }
      // keep the per-card marker honest on every repaint: drop it once unlocked or once the gap leaves the window
      if (st.done || st.gapBase <= 0 || st.gapBase > FREESHIP_MARKER_WINDOW) {
        var marks = this.root.querySelectorAll('.gbppu-card__fsmark');
        for (var mi = 0; mi < marks.length; mi++) { if (marks[mi].parentNode) marks[mi].parentNode.removeChild(marks[mi]); }
      }
      if (instant) { (window.requestAnimationFrame || window.setTimeout)(function () { bar.classList.remove('gbppu-fsbar--instant'); }); }
    },

    /* ---------------- show / hide -------------------------------------- */
    openDrawer: function (sourceBtn, offers) {
      this.sourceBtn = sourceBtn || this.sourceBtn;
      this.renderList(offers || []);
      this.lastFocus = document.activeElement;
      this.open = true; this.cartDirty = false; this.pending = [];
      this.root.hidden = false;
      this.root.setAttribute('aria-hidden', 'false');
      void this.root.offsetWidth;
      this.root.classList.add('is-open');
      document.documentElement.classList.add('gbppu-lock');
      document.body.classList.add('gbppu-lock');
      this.startTimer();
      if (this.panel) this.panel.setAttribute('tabindex', '-1');
      var first = this.root.querySelector('[data-gbppu-close]') || this.panel;
      if (first && first.focus) { try { first.focus(); } catch (e) {} }
      document.dispatchEvent(new CustomEvent('gbppu:opened'));
    },

    closeDrawer: function (restoreFocus) {
      this.open = false;
      this.deciding = false; this.bypass = false;   // never leave the submit-guard armed after the drawer closes
      this.root.classList.remove('is-open');
      document.documentElement.classList.remove('gbppu-lock');
      document.body.classList.remove('gbppu-lock');
      this.stopTimer();
      var root = this.root;
      window.setTimeout(function () {
        if (!GBPPU.open) { root.hidden = true; root.setAttribute('aria-hidden', 'true'); }
      }, 260);
      if (restoreFocus && this.lastFocus && this.lastFocus.focus) { try { this.lastFocus.focus(); } catch (e) {} }
    },

    // Safari/iOS commonly restores this page from bfcache after checkout. That restore includes the open
    // modal, PROCESSING label, pointer-blocking loading class, and open/deciding guards. Clear every
    // navigation-only state synchronously so the shopper returns to a usable cart and can check out again.
    resetAfterNavigation: function () {
      this.open = false; this.deciding = false; this.bypass = false;
      this.pending = []; this.cartDirty = false; this.cartPending = false;
      this._fsLockOnOpen = false; this._fsPendingBaseCents = 0; this._fsExpectedItems = 0;
      this._cartHtml = null; this.sourceBtn = null; this.lastFocus = null;
      this.stopTimer();

      if (window.__GBPPU_FALLBACK) { clearTimeout(window.__GBPPU_FALLBACK); window.__GBPPU_FALLBACK = null; }
      window.__GBPPU_PENDING = null;

      if (this.root) {
        this.root.classList.remove('is-open');
        this.root.hidden = true;
        this.root.setAttribute('aria-hidden', 'true');
        var cont = this.root.querySelector('[data-gbppu-continue]');
        var lbl = cont && cont.querySelector('.gbppu-continue__label');
        if (cont) {
          cont.classList.remove('is-loading', 'gbppu-continue--nudge');
          cont.removeAttribute('aria-busy');
          cont.disabled = false;
          if (lbl) lbl.textContent = cont._gbWas != null ? cont._gbWas : 'CHECKOUT';
          try { delete cont._gbWas; } catch (e) { cont._gbWas = null; }
        }
      }
      document.documentElement.classList.remove('gbppu-lock');
      document.body.classList.remove('gbppu-lock');
      this.neutralize();
    },

    // X / tap-outside: close + return to cart (with added items reflected)
    dismiss: function () {
      this.closeDrawer(true);
      if (!this.cartDirty) return;
      var self = this, done = false;
      var refresh = function () { if (done) return; done = true; self.refreshNativeCart(); };
      var pend = (this.pending || []).slice();
      if (pend.length) {
        (Promise.allSettled ? Promise.allSettled(pend) : Promise.all(pend.map(function (p) { return p.catch(function () {}); }))).then(refresh);
        window.setTimeout(refresh, 1500);
      } else { refresh(); }
    },

    /* ---------------- drawer interactions ------------------------------ */
    onRootClick: function (e) {
      try {
        var t = e.target;
        if (t.closest('[data-gbppu-close], .gbppu-overlay')) { e.preventDefault(); this.dismiss(); return; }
        if (t.closest('[data-gbppu-decline], [data-gbppu-continue]')) { e.preventDefault(); this.continueCheckout(); return; }
        if (t.closest('[data-gbppu-inc]')) { e.preventDefault(); this.stepQty(t.closest('[data-gbppu-card]'), +1); return; }
        if (t.closest('[data-gbppu-dec]')) { e.preventDefault(); this.stepQty(t.closest('[data-gbppu-card]'), -1); return; }
        if (t.closest('[data-gbppu-add]')) { e.preventDefault(); this.addCard(t.closest('[data-gbppu-card]')); return; }
      } catch (err) { if (window.console) console.warn('[GBPPU] click', err); }
    },

    onKeydown: function (e) {
      if (e.key !== 'Tab' || !this.open) return;
      var nodes = this.panel.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])');
      var list = Array.prototype.filter.call(nodes, function (el) { return !el.disabled && el.offsetParent !== null; });
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    },

    stepQty: function (card, delta) {
      if (!card) return;
      var input = card.querySelector('[data-gbppu-qty-input]');
      var v = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
      input.value = v; input.setAttribute('aria-label', 'Quantity ' + v);
    },

    addCard: function (card) {
      if (!card) return;
      var btn = card.querySelector('[data-gbppu-add]');
      if (btn._gbBusy) return;   // re-entrancy guard: ignore a rapid double-tap while an add is still in flight
      btn._gbBusy = true;
      var label = btn.querySelector('.gbppu-add__label');
      var input = card.querySelector('[data-gbppu-qty-input]');
      var variantId = card.getAttribute('data-variant-id');
      var saveCents = parseInt(card.getAttribute('data-save-cents'), 10) || 0;
      var qty = Math.max(1, parseInt(input.value, 10) || 1);
      var self = this;

      // OPTIMISTIC: flip instantly, fire add in background (Brightsport feel)
      this.cartDirty = true;
      // ACCUMULATE across rapid multi-adds: each optimistic paint is computed against the (still-stale) this.cart,
      // so a 2nd add before the /cart.js refresh would otherwise FORGET the 1st. Sum the pending add-cents + the
      // expected item_count; applyFreshCart() zeroes the accumulator only once the real cart reflects these adds.
      var addBaseCents = (parseInt(card.getAttribute('data-now-cents'), 10) || 0) * qty;
      this._fsPendingBaseCents = (this._fsPendingBaseCents || 0) + addBaseCents;
      this._fsExpectedItems = (this._fsExpectedItems || 0) + qty;
      this.paintFreeShip(this._fsPendingBaseCents);   // instant free-ship bar bump (accumulated)
      var cont = this.root && this.root.querySelector('[data-gbppu-continue]');              // post-add: pulse CHECKOUT to push the decision
      if (cont) { cont.classList.remove('gbppu-continue--nudge'); void cont.offsetWidth; cont.classList.add('gbppu-continue--nudge'); }
      btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy');
      btn.classList.add('is-added');
      if (label) {
        label.textContent = '✓ ' + (btn.getAttribute('data-added-label') || 'ADDED');
        if (btn._gbT) clearTimeout(btn._gbT);
        btn._gbT = window.setTimeout(function () { if (btn.classList.contains('is-added')) label.textContent = 'ADD MORE'; }, 1400);
      }

      // Kaching coexistence (Build-Registry R-pattern): '?kaching_bundles=true' makes
      // Kaching's _isInterceptableUrl() return false, so this add bypasses Kaching's
      // global window.fetch patch — no bundle-merge / dedupe / fallback-deal mangling.
      var p = fetch('/cart/add.js?kaching_bundles=true', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: qty, properties: { _ppu: '1', _ppu_save: String(saveCents) } }],
          sections: [self.cfg.cartSectionId], sections_url: window.location.pathname
        })
      })
        .then(function (r) { if (!r.ok) return r.json().then(function (j) { throw j; }); return r.json(); })
        .then(function (res) {
          if (res && res.sections && res.sections[self.cfg.cartSectionId]) self._cartHtml = res.sections[self.cfg.cartSectionId];
          // we just WROTE to the cart -> invalidate the shared cart-bus cache so the refresh reads the
          // NEW subtotal (otherwise the free-ship bar reverts to the stale pre-add value).
          if (window.GB && window.GB.CartBus && window.GB.CartBus.markDirty) window.GB.CartBus.markDirty('ppu-add');
          self.dispatchCartEvents();
        })
        .catch(function (err) {
          btn.classList.remove('is-added');
          if (btn._gbT) clearTimeout(btn._gbT);
          if (label) label.textContent = 'TRY AGAIN';
          // roll back THIS add's optimistic contribution so the bar + the reconciliation gate stay honest
          self._fsPendingBaseCents = Math.max(0, (self._fsPendingBaseCents || 0) - addBaseCents);
          self._fsExpectedItems = Math.max(0, (self._fsExpectedItems || 0) - qty);
          self.refreshCart();   // revert the optimistic free-ship bar back to the real cart state on a failed add
          if (window.console) console.warn('[GBPPU] add failed', err);
        });
      p.then(function () { btn._gbBusy = false; });   // clear re-entrancy guard once the add settles (allows intentional "ADD MORE")
      this.pending.push(p);
    },

    continueCheckout: function () {
      var self = this;
      // Navigate to /checkout WITHOUT closing the drawer first. Keeping the full-screen drawer UP means
      // the cart behind it never flashes during the redirect (it just goes drawer -> checkout). Show a
      // 'processing' state on the button so the brief pending-add wait feels intentional, not frozen.
      var cont = this.root && this.root.querySelector('[data-gbppu-continue]');
      var lbl = cont && cont.querySelector('.gbppu-continue__label');
      if (cont) cont.classList.add('is-loading');
      if (lbl && cont._gbWas == null) { cont._gbWas = lbl.textContent; lbl.textContent = 'PROCESSING…'; }
      var go = function () { self.go(); };   // drawer stays visible; the page navigates over it
      var pend = (this.pending || []).slice();
      if (pend.length) {
        var done = false, fin = function () { if (done) return; done = true; self.pending = []; go(); };
        (Promise.allSettled ? Promise.allSettled(pend) : Promise.all(pend.map(function (p) { return p.catch(function () {}); }))).then(fin);
        window.setTimeout(fin, 1500);
      } else { go(); }
    },

    /* ---------------- native cart refresh (safe, single node) ---------- */
    dispatchCartEvents: function () {
      var self = this;
      CART_EVENTS.forEach(function (ev) {
        try { document.dispatchEvent(new CustomEvent(ev, { detail: { cart: self.cart, source: 'gbppu' } })); } catch (e) {}
      });
    },

    refreshNativeCart: function () {
      var self = this;
      this.dispatchCartEvents();
      var apply = function (html) {
        try {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var from = doc.querySelector('cart-drawer-items') || doc.querySelector('#CartDrawer-CartItems');
          var to = document.querySelector('cart-drawer-items') || document.querySelector('#CartDrawer-CartItems');
          if (from && to) to.replaceWith(from);
        } catch (e) {}
      };
      if (this._cartHtml) { apply(this._cartHtml); this._cartHtml = null; return; }
      fetch(window.location.pathname + '?sections=' + this.cfg.cartSectionId, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (j) { if (j && j[self.cfg.cartSectionId]) apply(j[self.cfg.cartSectionId]); })
        .catch(function () {});
    },

    /* ---------------- countdown ---------------------------------------- */
    startTimer: function () {
      var el = this.root.querySelector('[data-gbppu-timer]');
      if (!el) return;
      var mins = this.cfg.timerMinutes || 10, start;
      try {
        start = parseInt(sessionStorage.getItem(this.SS_TIMER), 10);
        if (!start) { start = Date.now(); sessionStorage.setItem(this.SS_TIMER, String(start)); }
      } catch (e) { start = Date.now(); }
      var endAt = start + mins * 60000;
      var tick = function () {
        var left = Math.max(0, endAt - Date.now());
        if (left <= 0) {
          el.textContent = 'ORDER NOW';   // expired: swap the 00:00 for a CTA (Emi, Jun 26)
          if (GBPPU.timerInt) { clearInterval(GBPPU.timerInt); GBPPU.timerInt = null; }
          return;
        }
        var m = Math.floor(left / 60000), s = Math.floor((left % 60000) / 1000);
        el.textContent = (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
      };
      this.stopTimer(); tick(); this.timerInt = setInterval(tick, 1000);
    },
    stopTimer: function () { if (this.timerInt) { clearInterval(this.timerInt); this.timerInt = null; } }
  };

  window.GBPPU = GBPPU;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', GBPPU.init.bind(GBPPU));
  } else { GBPPU.init(); }
})();
